import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"

import { CodeBlock } from "@/components/dev/CodeBlock"
import { PropsTable } from "@/components/dev/PropsTable"
import { Button, LinkButton } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"

export const Route = createFileRoute("/qr/$qrLookupID/dev/atoms/button")({
  staticData: {
    pageName: "Button Component",
  },
  component: RouteComponent,
})

function RouteComponent() {
  const [clickCount, setClickCount] = useState(0)

  return (
    <>
      <style>{`
        .dev-container > *:not(:first-child) {
          margin-top: var(--space-5);
        }
      `}</style>
      <div className="dev-container">
        <Card>
          <Heading level={1} as={2}>
            Button Component
          </Heading>
          <Text>
            The Button component provides accessible, interactive buttons with
            multiple variants and states. Built on React Aria Components for
            full keyboard and screen reader support.
          </Text>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Button Types
          </Heading>
          <Text>
            Button types map through theme tokens for consistent styling across
            the app
          </Text>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-3)",
            }}
          >
            <div>
              <Text level={2} className="semi-bold">
                Primary Button
              </Text>
              <Text level={3}>Used for primary actions and CTAs</Text>
              <Button
                buttonType="primary"
                onPress={() => setClickCount(clickCount + 1)}
              >
                Click me ({clickCount})
              </Button>
              <CodeBlock>{`<Button buttonType="primary" onPress={() => {}}>
  Click me
</Button>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Default Button
              </Text>
              <Text level={3}>Standard button style (neutral theme)</Text>
              <Button
                buttonType="default"
                onPress={() => console.log("Default button clicked!")}
              >
                Default Action
              </Button>
              <CodeBlock>{`<Button buttonType="default" onPress={() => {}}>
  Default Action
</Button>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Success Button
              </Text>
              <Text level={3}>Used for positive/successful actions</Text>
              <Button
                buttonType="success"
                onPress={() => console.log("Success button clicked!")}
              >
                Confirm
              </Button>
              <CodeBlock>{`<Button buttonType="success" onPress={() => {}}>
  Confirm
</Button>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Danger Button
              </Text>
              <Text level={3}>Used for destructive actions</Text>
              <Button
                buttonType="danger"
                onPress={() => console.log("Danger button clicked!")}
              >
                Delete
              </Button>
              <CodeBlock>{`<Button buttonType="danger" onPress={() => {}}>
  Delete
</Button>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Caution Button
              </Text>
              <Text level={3}>Used for warning actions</Text>
              <Button
                buttonType="caution"
                onPress={() => console.log("Caution button clicked!")}
              >
                Warning
              </Button>
              <CodeBlock>{`<Button buttonType="caution" onPress={() => {}}>
  Warning
</Button>`}</CodeBlock>
            </div>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Presentations
          </Heading>
          <Text>Control the visual style of buttons</Text>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-3)",
            }}
          >
            <div>
              <Text level={2} className="semi-bold">
                Filled (Default)
              </Text>
              <Text level={3}>Solid filled button style</Text>
              <Button
                buttonType="primary"
                presentation="filled"
                onPress={() => console.log("Filled button clicked!")}
              >
                Filled Button
              </Button>
              <CodeBlock>{`<Button buttonType="primary" presentation="filled" onPress={() => {}}>
  Filled Button
</Button>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Ghost
              </Text>
              <Text level={3}>
                Transparent background with subtle hover effects
              </Text>
              <Button
                buttonType="primary"
                presentation="ghost"
                onPress={() => console.log("Ghost button clicked!")}
              >
                Ghost Button
              </Button>
              <CodeBlock>{`<Button buttonType="primary" presentation="ghost" onPress={() => {}}>
  Ghost Button
</Button>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Link
              </Text>
              <Text level={3}>Button styled as a text link</Text>
              <Button
                buttonType="primary"
                presentation="link"
                onPress={() => console.log("Link button clicked!")}
              >
                Link Button
              </Button>
              <CodeBlock>{`<Button buttonType="primary" presentation="link" onPress={() => {}}>
  Link Button
</Button>`}</CodeBlock>
            </div>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Buttons with Icons
          </Heading>
          <Text>
            Add icons to buttons using the Icon component with slot prop
          </Text>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-3)",
            }}
          >
            <div>
              <Text level={2} className="semi-bold">
                Icon on Left
              </Text>
              <Button
                buttonType="primary"
                onPress={() => console.log("Icon left button clicked!")}
              >
                <Icon name="parking" slot="left" />
                Buy Parking
              </Button>
              <CodeBlock>{`<Button buttonType="primary" onPress={() => {}}>
  <Icon name="parking" slot="left" />
  Buy Parking
</Button>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Icon on Right
              </Text>
              <Button
                buttonType="primary"
                onPress={() => console.log("Icon right button clicked!")}
              >
                Continue
                <Icon name="arrowRight" slot="right" />
              </Button>
              <CodeBlock>{`<Button buttonType="primary" onPress={() => {}}>
  Continue
  <Icon name="arrowRight" slot="right" />
</Button>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Icon Only
              </Text>
              <Text level={3}>
                Note: Use IconButton component for better accessibility with
                icon-only buttons
              </Text>
              <Button
                buttonType="primary"
                onPress={() => console.log("Icon only button clicked!")}
              >
                <Icon name="close" />
              </Button>
              <CodeBlock>{`<Button buttonType="primary" onPress={() => {}}>
  <Icon name="close" />
</Button>`}</CodeBlock>
            </div>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Button States
          </Heading>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-3)",
            }}
          >
            <div>
              <Text level={2} className="semi-bold">
                Disabled
              </Text>
              <Button
                buttonType="primary"
                isDisabled
                onPress={() => console.log("Disabled button clicked!")}
              >
                Disabled Button
              </Button>
              <CodeBlock>{`<Button buttonType="primary" isDisabled onPress={() => {}}>
  Disabled Button
</Button>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Pending (Loading)
              </Text>
              <Text level={3}>
                Shows loading spinner and disables interaction
              </Text>
              <Button
                buttonType="primary"
                isPending
                onPress={() => console.log("Pending button clicked!")}
              >
                Loading...
              </Button>
              <CodeBlock>{`<Button buttonType="primary" isPending onPress={() => {}}>
  Loading...
</Button>`}</CodeBlock>
            </div>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            LinkButton
          </Heading>
          <Text>
            LinkButton combines button styling with TanStack Router navigation.
            Use this for navigation actions instead of regular buttons with
            onClick handlers.
          </Text>
          <LinkButton
            buttonType="primary"
            from="/qr/$qrLookupID/dev/atoms/button"
            to="/qr/$qrLookupID/welcome"
          >
            <Icon name="arrowLeft" slot="left" />
            Go to Welcome
          </LinkButton>
          <CodeBlock>{`<LinkButton
  buttonType="primary"
  from="/qr/$qrLookupID/dev/atoms/button"
  to="/qr/$qrLookupID/welcome"
>
  <Icon name="arrowLeft" slot="left" />
  Go to Welcome
</LinkButton>`}</CodeBlock>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Combining Types and Presentations
          </Heading>
          <Text>
            Mix button types with different presentations for various effects
          </Text>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "var(--space-3)",
            }}
          >
            <Button
              buttonType="danger"
              presentation="ghost"
              onPress={() => console.log("Danger Ghost clicked!")}
            >
              Danger Ghost
            </Button>
            <Button
              buttonType="success"
              presentation="link"
              onPress={() => console.log("Success Link clicked!")}
            >
              Success Link
            </Button>
            <Button
              buttonType="caution"
              presentation="filled"
              onPress={() => console.log("Caution Filled clicked!")}
            >
              Caution Filled
            </Button>
          </div>
          <CodeBlock>{`<Button buttonType="danger" presentation="ghost" onPress={() => {}}>
  Danger Ghost
</Button>

<Button buttonType="success" presentation="link" onPress={() => {}}>
  Success Link
</Button>

<Button buttonType="caution" presentation="filled" onPress={() => {}}>
  Caution Filled
</Button>`}</CodeBlock>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "buttonType",
                type: '"primary" | "default" | "success" | "danger" | "caution"',
                default: '"default"',
                description: "Semantic button type that maps to theme tokens",
              },
              {
                name: "presentation",
                type: '"filled" | "ghost" | "link"',
                default: '"filled"',
                description: "Visual presentation style of the button",
              },
              {
                name: "onPress",
                type: "(e: PressEvent) => void",
                default: "undefined",
                description:
                  "Callback when button is pressed (from React Aria)",
              },
              {
                name: "isDisabled",
                type: "boolean",
                default: "false",
                description: "Whether button is disabled",
              },
              {
                name: "isPending",
                type: "boolean",
                default: "false",
                description:
                  "Whether button shows loading state and is disabled",
              },
              {
                name: "type",
                type: '"button" | "submit" | "reset"',
                default: '"button"',
                description: "HTML button type attribute",
              },
              {
                name: "className",
                type: "string",
                default: "undefined",
                description: "Additional CSS classes to apply",
              },
            ]}
          />
          <Text>
            Note: Button extends React Aria Components ButtonProps, so it
            supports all standard React Aria button props including focus,
            hover, and press events.
          </Text>
        </Card>
      </div>
    </>
  )
}
